<template>
  <div class="min-h-screen bg-gray-100 flex flex-col">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      :title="hospitalInfo.name"
      left-arrow
      @click-left="handleBack"
    >
      <template #right>
        <font-awesome-icon 
          icon="info-circle"
          class="text-lg"
          @click="showHospitalInfo = true"
        />
      </template>
    </van-nav-bar>

    <!-- 聊天内容区域 -->
    <div 
      ref="messageList"
      class="flex-1 overflow-y-auto p-4 space-y-4"
    >
      <!-- 系统提示 -->
      <div class="text-center text-sm text-gray-500">
        <p>请注意保护个人隐私，勿轻易透露敏感信息</p>
        <p>如需紧急救助，请立即拨打急诊电话</p>
      </div>

      <!-- 消息列表 -->
      <div
        v-for="message in messages"
        :key="message.id"
        :class="[
          'flex',
          message.senderId === userId ? 'justify-end' : 'justify-start'
        ]"
      >
        <!-- 头像 -->
        <img
          v-if="message.senderId !== userId"
          :src="message.avatar"
          class="w-8 h-8 rounded-full mr-2"
        >

        <!-- 消息气泡 -->
        <div
          :class="[
            'max-w-[70%] rounded-lg p-3',
            message.senderId === userId
              ? 'bg-primary text-white rounded-tr-none'
              : 'bg-white rounded-tl-none'
          ]"
        >
          <!-- 文本消息 -->
          <div v-if="message.type === 'text'" class="text-sm">
            {{ message.content }}
          </div>

          <!-- 图片消息 -->
          <div v-else-if="message.type === 'image'" class="w-48">
            <img 
              :src="message.content" 
              class="w-full rounded cursor-pointer"
              @click="previewImage(message.content)"
            >
          </div>

          <!-- 语音消息 -->
          <div 
            v-else-if="message.type === 'voice'"
            class="flex items-center space-x-2 cursor-pointer"
            @click="playVoice(message)"
          >
            <font-awesome-icon 
              :icon="message.isPlaying ? 'pause' : 'play'"
              class="text-sm"
            />
            <span class="text-sm">{{ message.duration }}″</span>
          </div>
        </div>

        <!-- 发送者头像 -->
        <img
          v-if="message.senderId === userId"
          :src="userAvatar"
          class="w-8 h-8 rounded-full ml-2"
        >
      </div>

      <!-- 对方正在输入 -->
      <div v-if="isTyping" class="flex justify-start">
        <div class="bg-white rounded-lg p-3 rounded-tl-none">
          <van-loading type="spinner" size="16" vertical>
            对方正在输入...
          </van-loading>
        </div>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="bg-white border-t p-2">
      <!-- 功能按钮区 -->
      <div class="flex justify-around mb-2">
        <div 
          class="text-center"
          @touchstart="startRecording"
          @touchend="stopRecording"
          @touchcancel="cancelRecording"
        >
          <font-awesome-icon :icon="isRecording ? 'microphone-alt' : 'microphone'" />
          <p class="text-xs">{{ isRecording ? '松开发送' : '按住说话' }}</p>
        </div>
        <div class="text-center" @click="$refs.imageInput.click()">
          <font-awesome-icon icon="image" />
          <p class="text-xs">图片</p>
        </div>
        <div class="text-center" @click="showQuickReply = true">
          <font-awesome-icon icon="comment-dots" />
          <p class="text-xs">快捷回复</p>
        </div>
      </div>

      <!-- 文本输入框 -->
      <div class="flex items-center space-x-2">
        <van-field
          v-model="inputMessage"
          type="textarea"
          autosize
          placeholder="请输入消息..."
          @focus="handleFocus"
          @keypress.enter.prevent="sendMessage"
        />
        <van-button 
          type="primary"
          size="small"
          :disabled="!inputMessage"
          @click="sendMessage"
        >
          发送
        </van-button>
      </div>

      <!-- 隐藏的文件输入 -->
      <input
        ref="imageInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleImageUpload"
      >
    </div>

    <!-- 医院信息弹窗 -->
    <van-popup
      v-model="showHospitalInfo"
      position="bottom"
      round
      class="h-[70vh]"
    >
      <div class="p-4">
        <h3 class="text-lg font-medium mb-4">{{ hospitalInfo.name }}</h3>
        <div class="space-y-4">
          <div class="flex items-center">
            <font-awesome-icon icon="clock" class="text-gray-400 mr-2" />
            <div>
              <p class="font-medium">营业时间</p>
              <p class="text-sm text-gray-500">{{ hospitalInfo.businessHours }}</p>
            </div>
          </div>
          <div class="flex items-center">
            <font-awesome-icon icon="phone" class="text-gray-400 mr-2" />
            <div>
              <p class="font-medium">联系电话</p>
              <p class="text-sm text-gray-500">{{ hospitalInfo.phone }}</p>
            </div>
          </div>
          <div class="flex items-center">
            <font-awesome-icon icon="map-marker-alt" class="text-gray-400 mr-2" />
            <div>
              <p class="font-medium">地址</p>
              <p class="text-sm text-gray-500">{{ hospitalInfo.address }}</p>
            </div>
          </div>
          <div>
            <p class="font-medium mb-2">医院简介</p>
            <p class="text-sm text-gray-500">{{ hospitalInfo.description }}</p>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 快捷回复弹窗 -->
    <van-action-sheet
      v-model="showQuickReply"
      title="快捷回复"
      :actions="quickReplies"
      @select="onQuickReplySelect"
    />

    <!-- 图片预览 -->
    <van-image-preview
      v-model="showImagePreview"
      :images="[previewImageUrl]"
    />
  </div>
</template>

<script>
export default {
  name: 'Chat',
  data() {
    return {
      hospitalInfo: {
        id: '',
        name: '正在加载...',
        businessHours: '',
        phone: '',
        address: '',
        description: ''
      },
      messages: [],
      inputMessage: '',
      isTyping: false,
      isRecording: false,
      showHospitalInfo: false,
      showQuickReply: false,
      showImagePreview: false,
      previewImageUrl: '',
      userId: null,
      userAvatar: '',
      quickReplies: [
        { name: '您好，请问有什么可以帮您？' },
        { name: '好的，我明白了' },
        { name: '请稍等，我查看一下' },
        { name: '建议您带宠物来医院做进一步检查' }
      ]
    }
  },
  methods: {
    async fetchHospitalInfo() {
      console.log('fetchHospitalInfo 方法被调用')
      
      // 使用模拟数据
      this.hospitalInfo = {
        id: '1',
        name: '爱宠动物医院',
        businessHours: '周一至周日 8:00-20:00',
        phone: '010-12345678',
        address: '北京市朝阳区建国路88号',
        description: '爱宠动物医院是一家专业的宠物医疗机构，提供全面的宠物医疗服务，包括疫苗接种、疾病诊断、手术治疗等。'
      }
    },
    async fetchHistoryMessages() {
      console.log('fetchHistoryMessages 方法被调用')
      
      // 使用模拟数据
      this.messages = [
        {
          id: '1',
          type: 'text',
          content: '您好，请问有什么可以帮您？',
          senderId: 'hospital-1',
          avatar: 'https://img01.yzcdn.cn/vant/cat.jpeg',
          timestamp: Date.now() - 3600000
        },
        {
          id: '2',
          type: 'text',
          content: '我家的猫咪最近食欲不振，请问可能是什么原因？',
          senderId: this.userId || 'user-1',
          timestamp: Date.now() - 3500000
        }
      ]
      
      this.scrollToBottom()
    },
    async sendMessage() {
      console.log('sendMessage 方法被调用')
      
      if (!this.inputMessage) return
      
      const message = {
        id: Date.now().toString(),
        type: 'text',
        content: this.inputMessage,
        senderId: this.userId || 'user-1',
        timestamp: Date.now()
      }
      
      this.messages.push(message)
      this.inputMessage = ''
      this.scrollToBottom()
      
      // 模拟回复
      setTimeout(() => {
        this.isTyping = true
        
        setTimeout(() => {
          this.isTyping = false
          
          this.messages.push({
            id: (Date.now() + 1).toString(),
            type: 'text',
            content: '感谢您的咨询，建议您尽快带宠物来院检查。',
            senderId: 'hospital-1',
            avatar: 'https://img01.yzcdn.cn/vant/cat.jpeg',
            timestamp: Date.now()
          })
          
          this.scrollToBottom()
        }, 2000)
      }, 1000)
    },
    async handleImageUpload(event) {
      console.log('handleImageUpload 方法被调用')
      
      const file = event.target.files[0]
      if (!file) return
      
      const message = {
        id: Date.now().toString(),
        type: 'image',
        content: URL.createObjectURL(file),
        senderId: this.userId || 'user-1',
        timestamp: Date.now()
      }
      
      this.messages.push(message)
      this.scrollToBottom()
      
      event.target.value = ''
    },
    startRecording() {
      console.log('startRecording 方法被调用')
      this.isRecording = true
    },
    async stopRecording() {
      console.log('stopRecording 方法被调用')
      this.isRecording = false
      
      // 模拟发送语音消息
      const message = {
        id: Date.now().toString(),
        type: 'voice',
        content: 'audio-url',
        duration: 3,
        senderId: this.userId || 'user-1',
        timestamp: Date.now()
      }
      
      this.messages.push(message)
    },
    cancelRecording() {
      console.log('cancelRecording 方法被调用')
      this.isRecording = false
    },
    handleFocus() {
      this.scrollToBottom()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageList
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    previewImage(url) {
      this.previewImageUrl = url
      this.showImagePreview = true
    },
    onQuickReplySelect(reply) {
      this.inputMessage = reply.name
      this.showQuickReply = false
      this.sendMessage()
    },
    handleBack() {
      console.log('handleBack 方法被调用')
      this.$router.back()
    },
    playVoice(message) {
      console.log('playVoice 方法被调用', message)
      message.isPlaying = !message.isPlaying
    }
  },
  async created() {
    console.log('Chat 页面已创建')
    this.userId = 'user-1'
    this.userAvatar = 'https://img01.yzcdn.cn/vant/dog.jpeg'
    
    await this.fetchHospitalInfo()
    await this.fetchHistoryMessages()
  }
}
</script>

<style scoped>
.van-field {
  flex: 1;
}
</style> 